import { useQueries } from "@tanstack/react-query";

import type { CopilotResourceRef } from "src/clients/agent-management";
import { useEntityResolutionContext } from "src/unifiedCopilot/chat/useEntityResolutionContext";
import {
  type ContextualEntityResolutionQuery,
  entityResolutionQueryOptions,
} from "src/unifiedCopilot/context/entityResolution";
import { useContextualEntities } from "src/unifiedCopilot/context/internalHooks";

/**
 * What a mention pill is called, from the sources that know the resource in
 * order of freshness: the catalogue's name when the page has it registered,
 * else the name the type's own resolver put on the ref, else the label the
 * message carried. Resolution is the same one the deeplink runs, so the name
 * shares its request.
 */
export const useResourceMentionLabel = (
  ref: CopilotResourceRef | null,
  fallback: string,
): string => {
  const entities = useContextualEntities();
  const context = useEntityResolutionContext();

  const catalogued = ref
    ? entities.find(
        (entity) => entity.type === ref.type && entity.id === ref.id,
      )
    : undefined;
  const resolution =
    ref && context && catalogued === undefined
      ? entityResolutionQueryOptions(ref, context)
      : undefined;
  // One query for a ref nobody has named yet, and none at all otherwise: a
  // catalogued pill resolves nothing, and neither does a type with no
  // resolver.
  const queries: ContextualEntityResolutionQuery[] = resolution
    ? [resolution]
    : [];
  const [resolved] = useQueries({ queries });

  return catalogued?.displayName ?? resolved?.data?.displayName ?? fallback;
};
